import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Activity, Save, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Inventories() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [stock, setStock] = useState([]); // [{product_id, site_id, quantity}]
  const [siteId, setSiteId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [counts, setCounts] = useState({}); // {product_id: "string"}
  const [filter, setFilter] = useState("");

  const load = async () => {
    const [i, p, s, st] = await Promise.all([
      api.get("/inventories"),
      api.get("/products"),
      api.get("/sites"),
      api.get("/stock"),
    ]);
    setList(i.data); setProducts(p.data); setSites(s.data); setStock(st.data);
    if (!siteId && s.data.length === 1) setSiteId(s.data[0].id);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);

  // theoretical stock for currently selected site, keyed by product_id
  const theoMap = useMemo(() => {
    const m = {};
    for (const r of stock) if (r.site_id === siteId) m[r.product_id] = r.quantity;
    return m;
  }, [stock, siteId]);

  // when site changes, pre-fill counts with theoretical for ALL products (0 if none)
  useEffect(() => {
    if (!siteId) return;
    const next = {};
    for (const p of products) next[p.id] = String(theoMap[p.id] ?? 0);
    setCounts(next);
  }, [siteId, products, theoMap]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => [p.name, p.sku, p.category].join(" ").toLowerCase().includes(q));
  }, [products, filter]);

  const submit = async () => {
    if (!siteId) return toast.error("Sélectionnez un site");
    const payload = {
      site_id: siteId,
      date,
      counts: Object.entries(counts)
        .filter(([_, v]) => v !== "" && v != null)
        .map(([product_id, physical_quantity]) => ({ product_id, physical_quantity: Number(physical_quantity) })),
    };
    if (!payload.counts.length) return toast.error("Saisissez au moins une quantité");
    try {
      await api.post("/inventories", payload);
      toast.success("Inventaire enregistré");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetToTheoretical = () => {
    const next = {};
    for (const p of products) next[p.id] = String(theoMap[p.id] ?? 0);
    setCounts(next);
    toast.success("Quantités réinitialisées au stock théorique");
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventaires journaliers"
        subtitle="Comptage physique · Détection d'écarts"
      />

      {/* Help banner */}
      <div className="surface p-4 flex gap-3 items-start" data-testid="inventory-help">
        <Info size={16} className="text-[var(--primary,#C2569B)] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[var(--text-muted)] leading-relaxed">
          <span className="font-medium text-[var(--text-base,#0a0a0a)]">Comment ça marche ?</span> Le <b>stock théorique</b> est calculé
          automatiquement (imports + entrées − ventes − sorties). Le champ <b>"Qté physique"</b> est pré-rempli avec le théorique :
          ne changez que les lignes où le comptage réel diffère. L'<b>écart</b> est affiché en direct.
        </div>
      </div>

      {canEdit && (
        <Surface
          title="Nouveau comptage"
          action={
            <div className="flex gap-2">
              <button
                data-testid="reset-theoretical"
                type="button"
                onClick={resetToTheoretical}
                className="btn-ghost text-xs"
              >
                Réinit. au théorique
              </button>
              <button data-testid="submit-inventory" onClick={submit} className="btn-primary flex items-center gap-2">
                <Save size={14} />Enregistrer
              </button>
            </div>
          }
        >
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="label-mono block mb-1.5">Site</label>
                <select
                  data-testid="inv-site"
                  required
                  className="input-base"
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                >
                  <option value="">— Choisir —</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Date</label>
                <input
                  type="date"
                  className="input-base"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-mono block mb-1.5">Filtrer (SKU, nom, catégorie)</label>
                <input
                  data-testid="inv-filter"
                  className="input-base"
                  placeholder="Tapez pour filtrer..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
            </div>

            {!siteId ? (
              <Empty message="Sélectionnez un site pour démarrer le comptage" />
            ) : (
              <div className="border border-[var(--border-line)] rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 label-mono bg-[var(--bg-soft,#f7f7f7)] border-b border-[var(--border-line)]">
                  <div className="col-span-5">Produit</div>
                  <div className="col-span-2 text-right">Théorique</div>
                  <div className="col-span-3 text-right">Qté physique</div>
                  <div className="col-span-2 text-right">Écart</div>
                </div>
                <div className="max-h-[28rem] overflow-auto divide-y divide-[var(--border-line)]">
                  {filtered.map((p) => {
                    const theo = theoMap[p.id] ?? 0;
                    const raw = counts[p.id];
                    const phys = raw === "" || raw == null ? null : Number(raw);
                    const gap = phys == null ? null : phys - theo;
                    return (
                      <div key={p.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-sm">
                        <div className="col-span-5 min-w-0">
                          <div className="font-medium truncate">{p.name}</div>
                          <div className="font-data text-xs text-[var(--text-muted)]">{p.sku}</div>
                        </div>
                        <div className="col-span-2 text-right font-data text-[var(--text-muted)]" data-testid={`theo-${p.sku}`}>
                          {theo}
                        </div>
                        <div className="col-span-3">
                          <input
                            data-testid={`inv-${p.sku}`}
                            type="number"
                            min="0"
                            className="input-base w-full text-right"
                            value={counts[p.id] ?? ""}
                            onChange={(e) => setCounts({ ...counts, [p.id]: e.target.value })}
                          />
                        </div>
                        <div
                          className={`col-span-2 text-right font-data ${
                            gap == null ? "text-[var(--text-muted)]" : gap === 0 ? "neon-text-green" : "neon-text-pink"
                          }`}
                          data-testid={`gap-${p.sku}`}
                        >
                          {gap == null ? "—" : gap > 0 ? `+${gap}` : gap}
                        </div>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <div className="p-4 text-sm text-[var(--text-muted)]">Aucun produit</div>}
                </div>
              </div>
            )}
          </div>
        </Surface>
      )}

      <Surface title="Inventaires précédents">
        {list.length === 0 ? (
          <Empty />
        ) : (
          <div className="divide-y divide-[var(--border-line)]">
            {list.map((inv) => (
              <details key={inv.id} className="px-5 py-3">
                <summary className="cursor-pointer flex items-center gap-3 text-sm">
                  <Activity size={14} className="text-[var(--neon-cyan)]" />
                  <span className="font-data text-[var(--text-muted)]">{inv.date}</span>
                  <span className="font-medium">{siteMap[inv.site_id]?.name}</span>
                  {inv.discrepancies > 0 ? (
                    <span className="badge-warn ml-auto">{inv.discrepancies} ÉCART(S)</span>
                  ) : (
                    <span className="badge-success ml-auto">CONFORME</span>
                  )}
                </summary>
                <div className="mt-3">
                  <table className="data-table">
                    <thead><tr><th>Produit</th><th>Théorique</th><th>Physique</th><th>Écart</th></tr></thead>
                    <tbody>
                      {inv.rows.map((r, i) => (
                        <tr key={r.product_id || `inv-row-${i}`}>
                          <td>{productMap[r.product_id]?.name || r.product_id}</td>
                          <td className="font-data text-[var(--text-muted)]">{r.theoretical}</td>
                          <td className="font-data">{r.physical}</td>
                          <td className={`font-data ${r.gap === 0 ? "neon-text-green" : "neon-text-pink"}`}>
                            {r.gap > 0 ? "+" : ""}{r.gap}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        )}
      </Surface>
    </div>
  );
}
